import React from 'react';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import ArticlesPage from '@fuww/library/src/Layouts/ArticleListPage';
import AuthorBiography from '@fuww/library/src/AuthorBiography';
import { camelCase, upperFirst } from 'lodash-es';
import { FetchMoreQueryOptions } from '@apollo/client';
import Head from '../components/Head';
import HomeLink from '../components/HomeLink';
import LinkAndAnchor from '../components/LinkAndAnchor';
import ArticleList from '../components/News/Article/List';
import withApollo from '../lib/apollo';
import { useSiteContext } from '../components/SiteContext';
import messages from '../lib/messages.json';
import authorQuery from '../lib/queries/news/authorQuery.graphql';
import throw404 from '../lib/throw404';
import withQuery from '../lib/withQuery';
import {
  newsFeedImageConfigurations,
  authorPageImageConfigurations,
} from '../lib/imageConfigurations';
import {
  AuthorByEmailQuery,
  AuthorByEmailQueryVariables,
} from '../lib/graphql/api/graphql';
import ImageWithUrls from '../components/ImageWithUrls';
import getSourceSet from '../lib/getSourceSet';

const getAuthorSubjectType = (
  prefix: string,
  hasLocalNews: boolean,
) => (
  hasLocalNews
    ? upperFirst(`${camelCase(prefix)}AuthorByEmail`)
    : 'AuthorByEmail'
);

interface AuthorProperties {
  data: AuthorByEmailQuery;
  variables: AuthorByEmailQueryVariables;
  fetchMore: (variables: FetchMoreQueryOptions<
    AuthorByEmailQueryVariables
  >) => void;
  loading: boolean;
  showLoader: boolean;
}

const Author = ({
  data, variables, fetchMore, loading, showLoader,
}: AuthorProperties) => {
  const intl = useIntl();
  const { query: { authorSlug } } = useRouter();
  const { prefix, hasLocalNews } = useSiteContext();

  const author = data.universalAuthorByEmail;

  if (!author) return throw404();

  const {
    name,
    id,
    bioHtml,
    imageUrls,
  } = author;

  const authorSourceSet = getSourceSet(
    variables.authorImageConfigurations,
    imageUrls,
  );

  const breadcrumbs = [
    <HomeLink />,
    intl.formatMessage(messages.news),
    <LinkAndAnchor
      route="author"
      params={{ authorSlug }}
    >
      {intl.formatMessage(messages.allArticlesBy, { author: name })}
    </LinkAndAnchor>,
  ];

  return (
    <ArticlesPage
      breadcrumbs={breadcrumbs}
      title={intl.formatMessage(messages.allArticlesBy, { author: name })}
      topSlot={bioHtml && (
        <AuthorBiography
          bio={bioHtml}
          authorImage={
            imageUrls[0] && (
              <ImageWithUrls
                imageUrls={imageUrls as string[]}
                srcSet={authorSourceSet}
                sizes="(min-width: 600px) 120px, 90px"
                alt={`Image of ${name}`}
              />
            )
          }
        />
      )}
    >
      <Head
        title={intl.formatMessage(messages.allArticlesBy, { author: name })}
        description={
          intl.formatMessage(messages.allArticlesBy, { author: name })
        }
        subjectType={getAuthorSubjectType(prefix, hasLocalNews)}
        subjectId={id}
      />
      <ArticleList
        variables={variables}
        fetchMore={fetchMore}
        data={data}
        loading={loading}
        showLoader={showLoader}
      />
    </ArticlesPage>
  );
};

const AuthorWithQuery = withQuery<
  AuthorByEmailQuery,
  AuthorByEmailQueryVariables,
  false,
  { fieldname: Exclude<keyof AuthorByEmailQuery, '__typename'> }
>(Author);

const AuthorPage = () => {
  const { query: { authorSlug } } = useRouter();
  const {
    newsboard, locale,
  } = useSiteContext();

  return (
    <AuthorWithQuery
      query={authorQuery}
      variables={{
        newsboard,
        locale,
        email: `${authorSlug}@fashionunited.com`,
        first: 10,
        imageConfigurations: newsFeedImageConfigurations,
        authorImageConfigurations: authorPageImageConfigurations,
      }}
      errorMessage="Error while loading author page..."
    />
  );
};

export default withApollo(AuthorPage);
